/**
 * Цвета клубов и генератор эмблем.
 * Если цвета/логотип не заданы администратором, они выводятся
 * детерминированно из названия команды — «рандомные», но стабильные.
 */

function hash(input: string): number {
  let value = 2166136261;
  for (let i = 0; i < input.length; i += 1) {
    value ^= input.charCodeAt(i);
    value = Math.imul(value, 16777619);
  }
  return Math.abs(value);
}

export function initials(name: string): string {
  const words = name
    .replace(/[«»"'().,]/g, " ")
    .split(/\s+/)
    .filter(Boolean);
  if (!words.length) return "ФК";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
}

/** Наборы из трёх цветов: основной, средний, тёмный. */
const PALETTES: [string, string, string][] = [
  ["#e11d48", "#fb7185", "#7f1d1d"],
  ["#2563eb", "#60a5fa", "#1e3a8a"],
  ["#16a34a", "#4ade80", "#14532d"],
  ["#f59e0b", "#fcd34d", "#78350f"],
  ["#7c3aed", "#a78bfa", "#3b0764"],
  ["#0891b2", "#22d3ee", "#164e63"],
  ["#db2777", "#f472b6", "#831843"],
  ["#65a30d", "#a3e635", "#365314"],
  ["#ea580c", "#fb923c", "#7c2d12"],
  ["#0d9488", "#2dd4bf", "#134e4a"],
  ["#4f46e5", "#818cf8", "#312e81"],
  ["#b91c1c", "#f87171", "#450a0a"],
  ["#0369a1", "#38bdf8", "#082f49"],
  ["#a16207", "#facc15", "#422006"],
  ["#475569", "#94a3b8", "#0f172a"],
  ["#166534", "#22c55e", "#052e16"],
];

export const MAX_TEAM_COLORS = 3;

/** Нормализует список цветов: только #hex, не больше трёх. */
export function normalizeColors(colors: unknown): string[] {
  if (!Array.isArray(colors)) return [];
  return colors
    .map((color) => (typeof color === "string" ? color.trim() : ""))
    .filter((color) => /^#[0-9a-fA-F]{6}$/.test(color))
    .slice(0, MAX_TEAM_COLORS);
}

/** Случайный (детерминированный) набор цветов для команды. */
export function randomColors(name: string): string[] {
  const palette = PALETTES[hash(name) % PALETTES.length];
  const count = 2 + (hash(`n${name}`) % 2);
  return palette.slice(0, count);
}

export type LogoPattern =
  | "solid"
  | "stripes"
  | "pinstripes"
  | "hoops"
  | "sash"
  | "halves"
  | "chevron"
  | "quarters"
  | "diagonal"
  | "checker"
  | "wave"
  | "frame";

/** Варианты дизайна формы — используются и в эмблеме, и в футболке. */
export const KIT_PATTERNS: { id: LogoPattern; label: string }[] = [
  { id: "solid", label: "Однотонная" },
  { id: "stripes", label: "Вертикальные полосы" },
  { id: "pinstripes", label: "Тонкие полосы" },
  { id: "hoops", label: "Горизонтальные полосы" },
  { id: "sash", label: "Диагональная лента" },
  { id: "halves", label: "Две половины" },
  { id: "chevron", label: "Шеврон" },
  { id: "quarters", label: "Четверти" },
  { id: "diagonal", label: "Диагональные полосы" },
  { id: "checker", label: "Шахматка" },
  { id: "wave", label: "Волна" },
  { id: "frame", label: "Кант" },
];

const PATTERNS: LogoPattern[] = KIT_PATTERNS.map((item) => item.id);

export function isKitPattern(value: unknown): value is LogoPattern {
  return typeof value === "string" && (PATTERNS as string[]).includes(value);
}

/** Случайный (детерминированный) рисунок эмблемы — запасной вариант. */
export function randomPattern(name: string): LogoPattern {
  return PATTERNS[hash(`p${name}`) % PATTERNS.length];
}

/** Узор формы команды: сохранённый или запасной детерминированный. */
export function resolveKitPattern(name: string, pattern?: string | null): LogoPattern {
  return isKitPattern(pattern) ? pattern : randomPattern(name);
}

/**
 * По-настоящему случайный стиль команды (цвета + дизайн формы).
 * Используется при первичной генерации и по кнопке «Случайный стиль».
 */
export function randomTeamStyle(): { colors: string[]; kitPattern: LogoPattern } {
  const palette = PALETTES[Math.floor(Math.random() * PALETTES.length)];
  const roll = Math.random();
  let colors: string[];
  if (roll < 0.35) {
    // контрастная пара: основной цвет + белый + тёмный
    colors = [palette[0], "#f8fafc", palette[2]];
  } else if (roll < 0.7) {
    colors = [...palette];
  } else {
    // сочетание двух палитр
    const other = PALETTES[Math.floor(Math.random() * PALETTES.length)];
    colors = [palette[0], other[0], palette[2]];
  }
  const kitPattern = PATTERNS[Math.floor(Math.random() * PATTERNS.length)];
  return { colors, kitPattern };
}

/** Итоговая палитра команды: заданная админом или сгенерированная. */
export function resolveTeamColors(name: string, colors?: string[]): string[] {
  const valid = normalizeColors(colors);
  return valid.length ? valid : randomColors(name);
}

export type TournamentAccent = {
  hue: number;
  primary: string;
  soft: string;
  glow: string;
};

export function tournamentAccent(name: string): TournamentAccent {
  const h = hash(name) % 360;
  return {
    hue: h,
    primary: `hsl(${h} 72% 55%)`,
    soft: `hsl(${h} 60% 18%)`,
    glow: `hsl(${h} 85% 60% / 0.45)`,
  };
}

/** Строки таблицы CSV в кодировке, понятной Excel (BOM + разделитель «;»). */
export function toCsv(rows: (string | number)[][]): string {
  return (
    "\uFEFF" +
    rows
      .map((row) =>
        row
          .map((cell) => {
            const value = String(cell ?? "");
            return /[";\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
          })
          .join(";"),
      )
      .join("\r\n")
  );
}
